import { useId, type Ref } from 'react';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Textarea,
} from '@monksflow/monks-ui';

interface PersonaTextFieldProps {
  ref?: Ref<HTMLInputElement> | Ref<HTMLTextAreaElement>;
  name?: string;
  value?: string | number | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  label?: string;
  placeholder?: string;
  /** Helper copy under the control. Suppressed while `error` is set. */
  description?: string;
  /** Validation message. Present means the control renders as invalid. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  /** Fixed textarea height. Mutually exclusive with `maxRows`. */
  rows?: number;
  /** Grows with its content up to this many lines. */
  maxRows?: 4 | 8;
  /** Layout only — the control owns its own appearance. */
  className?: string;
  'aria-label'?: string;
}

/**
 * The ceiling is `maxRows * 20px` of line box plus the control's 16px of
 * vertical padding, expressed on Tailwind's 4px scale.
 */
const AUTO_GROW_MAX_HEIGHT = {
  4: 'max-h-24',
  8: 'max-h-44',
} as const;

/**
 * A labelled text control for the persona forms: `Field` chrome around either an
 * `Input` or a `Textarea`, with the label wired to the control by id so the
 * field keeps an accessible name.
 */
export function PersonaTextField(props: PersonaTextFieldProps) {
  const {
    ref,
    label,
    description,
    error,
    required,
    multiline,
    rows,
    maxRows,
    className,
    value,
    ...controlProps
  } = props;

  const generatedId = useId();
  const id = `${generatedId}-control`;
  const errorId = `${generatedId}-error`;
  const descriptionId = `${generatedId}-description`;
  const helperId = error ? errorId : description ? descriptionId : undefined;

  const shared = {
    ...controlProps,
    id,
    required,
    // `null` is what react-hook-form hands over for an unset optional field, and
    // it would flip the control to uncontrolled mid-edit.
    ...(value === undefined ? {} : { value: value ?? '' }),
    'aria-invalid': error ? true : undefined,
    'aria-describedby': helperId,
  };

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id}>
          {label}
          {required && <span className="text-destructive">*</span>}
        </FieldLabel>
      )}

      {multiline ? (
        <Textarea
          {...shared}
          ref={ref as Ref<HTMLTextAreaElement>}
          rows={rows ?? 2}
          error={Boolean(error)}
          className={maxRows ? `field-sizing-content ${AUTO_GROW_MAX_HEIGHT[maxRows]}` : ''}
        />
      ) : (
        <Input {...shared} ref={ref as Ref<HTMLInputElement>} />
      )}

      {error ? (
        <FieldError id={errorId}>{error}</FieldError>
      ) : description ? (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      ) : null}
    </Field>
  );
}
